import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  Building2,
  Check,
  Copy,
  Globe,
  Loader2,
  Mail,
  Palette,
  Plus,
  RefreshCw,
  Users,
} from 'lucide-react';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS } from '../../constants/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

/**
 * Organizations — platform-admin onboarding without the CLI.
 *
 * Create an org and assign its first org administrator in one step: an
 * existing account is attached directly (claims minted server-side); an
 * unknown email gets an email-bound invitation whose accept link is shown
 * here for copy/paste (email delivery is best-effort). This is the UI
 * replacement for scripts/bootstrap_academic_org.js in routine use — the
 * script remains the break-glass path for a fresh project with no admin.
 */

interface PendingInvite {
  token: string;
  invitedEmail: string;
  orgRole?: 'orgAdmin' | 'member';
  expired: boolean;
}

/** One person in the organisation, as listOrganizations returns them. */
interface OrgMember {
  userId: string;
  displayName: string;
  email: string;
  orgRole: 'orgAdmin' | 'member';
}

interface OrgBrandingRow {
  displayName?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
}

interface OrgRow {
  orgId: string;
  name: string;
  domains: string[];
  branding: OrgBrandingRow;
  invitePolicy: 'open' | 'domain-restricted';
  status: string;
  memberCount: number;
  /** Absent from a server that predates the member list; the count still shows. */
  members?: OrgMember[];
  pendingInvites: PendingInvite[];
}

interface InviteIssue {
  token: string;
  acceptUrl: string;
  emailQueued: boolean;
}

interface ProvisionResult {
  orgId: string;
  orgAdminUid: string | null;
  invite: InviteIssue | null;
}

function acceptLink(orgId: string, token: string): string {
  return `${window.location.origin}/org-invite/${token}?org=${encodeURIComponent(orgId)}`;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700 transition-colors shrink-0"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied' : 'Copy link'}
    </button>
  );
}

export default function AdminOrganizations() {
  const { isSuperAdmin } = useAuth();
  const { showToast } = useToast();
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [invitePolicy, setInvitePolicy] = useState<'open' | 'domain-restricted'>('open');
  const [domains, setDomains] = useState('');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<{
    orgId: string;
    adminEmail: string;
    attached: boolean;
    invite: InviteIssue | null;
  } | null>(null);

  // Per-org "invite members" inline form
  const [inviteFor, setInviteFor] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'orgAdmin'>('member');
  const [inviting, setInviting] = useState(false);

  // Per-org branding editor (Tier-1: name/logo/colors — data, not code)
  const [brandFor, setBrandFor] = useState<string | null>(null);
  const [brandForm, setBrandForm] = useState<OrgBrandingRow>({});
  const [savingBrand, setSavingBrand] = useState(false);

  const saveBranding = async (orgId: string) => {
    setSavingBrand(true);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_ORG_BRANDING);
      await fn({
        orgId,
        branding: {
          displayName: brandForm.displayName ?? '',
          logoUrl: brandForm.logoUrl ?? '',
          primaryColor: brandForm.primaryColor ?? '',
          accentColor: brandForm.accentColor ?? '',
        },
      });
      showToast('Branding saved — members see it on their next page load', 'success');
      setBrandFor(null);
      await loadOrgs();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to save branding', 'error');
    } finally {
      setSavingBrand(false);
    }
  };

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.LIST_ORGANIZATIONS);
      const res = await fn({});
      setOrgs((res.data as { organizations: OrgRow[] }).organizations);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to load organizations', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  const handleCreate = async () => {
    if (!name.trim() || !slug.trim() || !adminEmail.trim()) {
      showToast('Name, slug and admin email are required', 'error');
      return;
    }
    setCreating(true);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.PROVISION_ORGANIZATION);
      const res = await fn({
        orgId: slug.trim(),
        name: name.trim(),
        orgAdminEmail: adminEmail.trim(),
        invitePolicy,
        domains: domains
          .split(',')
          .map((d) => d.trim())
          .filter(Boolean),
        baseUrl: window.location.origin,
      });
      const data = res.data as ProvisionResult;
      setResult({
        orgId: data.orgId,
        adminEmail: adminEmail.trim().toLowerCase(),
        attached: !!data.orgAdminUid,
        invite: data.invite,
      });
      setShowCreate(false);
      setName('');
      setSlug('');
      setSlugEdited(false);
      setAdminEmail('');
      setDomains('');
      await loadOrgs();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to create organization', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (
    orgId: string,
    email: string,
    role: 'member' | 'orgAdmin' = 'member',
  ) => {
    setInviting(true);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.INVITE_ORG_ADMIN);
      const res = await fn({ orgId, email, role, baseUrl: window.location.origin });
      const data = res.data as InviteIssue & { invitedEmail: string; orgRole?: 'member' | 'orgAdmin' };
      setResult({
        orgId,
        adminEmail: data.invitedEmail,
        attached: false,
        invite: data,
      });
      setInviteFor(null);
      setInviteEmail('');
      setInviteRole('member');
      await loadOrgs();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to send invitation', 'error');
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Organizations</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadOrgs}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setShowCreate((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm font-medium text-white transition-colors"
            >
              <Plus className="w-4 h-4" />
              New organization
            </button>
          )}
        </div>
      </div>

      {/* Result panel — the invite link lives here; email is best-effort. */}
      {result && (
        <div className="mb-6 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-emerald-900">
                {result.attached ? (
                  <>
                    <span className="font-semibold">{result.adminEmail}</span> is now a
                    member of <span className="font-semibold">{result.orgId}</span>. They
                    should sign out and back in to pick up their new access.
                  </>
                ) : (
                  <>
                    Invitation ready for <span className="font-semibold">{result.adminEmail}</span>{' '}
                    to join <span className="font-semibold">{result.orgId}</span>
                    {result.invite?.emailQueued
                      ? ' — an email was sent, and you can also share the link directly:'
                      : ' — email delivery is not configured, so share this link with them directly:'}
                  </>
                )}
              </p>
              {!result.attached && result.invite && (
                <div className="mt-2 flex items-center gap-2">
                  <code className="text-xs bg-white ring-1 ring-emerald-200 rounded px-2 py-1 truncate">
                    {acceptLink(result.orgId, result.invite.token)}
                  </code>
                  <CopyButton text={acceptLink(result.orgId, result.invite.token)} />
                </div>
              )}
            </div>
            <button
              onClick={() => setResult(null)}
              className="text-xs text-emerald-700 hover:text-emerald-900 shrink-0"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {isSuperAdmin && showCreate && (
        <div className="mb-6 rounded-xl bg-white ring-1 ring-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Create organization</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display name</label>
              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugEdited) setSlug(slugify(e.target.value));
                }}
                placeholder="Stanford Cardiology Lab"
                className="w-full rounded-lg border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Slug (permanent ID)
              </label>
              <input
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugEdited(true);
                }}
                placeholder="stanford-cardio-lab"
                className="w-full rounded-lg border-gray-300 text-sm font-mono focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Organization admin email
              </label>
              <input
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                type="email"
                placeholder="pi@university.edu"
                className="w-full rounded-lg border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                An existing account is attached immediately; otherwise they get an invite link
                (works before they've signed up).
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Invite policy</label>
              <select
                value={invitePolicy}
                onChange={(e) => setInvitePolicy(e.target.value as 'open' | 'domain-restricted')}
                className="w-full rounded-lg border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="open">Open — project managers can invite any email</option>
                <option value="domain-restricted">
                  Domain-restricted — invites limited to the org's domains
                </option>
              </select>
            </div>
            {invitePolicy === 'domain-restricted' && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Email domains (comma-separated)
                </label>
                <input
                  value={domains}
                  onChange={(e) => setDomains(e.target.value)}
                  placeholder="university.edu, med.university.edu"
                  className="w-full rounded-lg border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <button
              onClick={() => setShowCreate(false)}
              className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm font-medium text-white transition-colors"
            >
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              Create &amp; assign admin
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : orgs.length === 0 ? (
        <div className="text-center py-16 text-sm text-gray-500">
          No organizations yet. Create one to onboard its administrator.
        </div>
      ) : (
        <div className="space-y-4">
          {orgs.map((org) => (
            <div key={org.orgId} className="rounded-xl bg-white ring-1 ring-gray-200 shadow-sm p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900">{org.name}</h3>
                    <code className="text-xs text-gray-500">{org.orgId}</code>
                    {org.status !== 'active' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                        {org.status}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {org.memberCount} member{org.memberCount === 1 ? '' : 's'}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Globe className="w-3.5 h-3.5" />
                      {org.invitePolicy === 'open'
                        ? 'Open invites'
                        : org.domains.length
                          ? org.domains.join(', ')
                          : 'Domain-restricted (no domains set)'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setBrandFor(brandFor === org.orgId ? null : org.orgId);
                      setBrandForm(org.branding || {});
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700 transition-colors"
                  >
                    {org.branding?.primaryColor && (
                      <span
                        className="w-3 h-3 rounded-full ring-1 ring-gray-300"
                        style={{ backgroundColor: org.branding.primaryColor }}
                      />
                    )}
                    <Palette className="w-3.5 h-3.5" />
                    Branding
                  </button>
                  <button
                    onClick={() => {
                      setInviteFor(inviteFor === org.orgId ? null : org.orgId);
                      setInviteEmail('');
                      setInviteRole('member');
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700 transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    Invite members to the organization
                  </button>
                </div>
              </div>

              {brandFor === org.orgId && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-gray-100 pt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Display name (app chrome & tab title)
                    </label>
                    <input
                      value={brandForm.displayName ?? ''}
                      onChange={(e) => setBrandForm({ ...brandForm, displayName: e.target.value })}
                      placeholder={org.name}
                      className="w-full rounded-lg border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Logo URL (https, square works best)
                    </label>
                    <input
                      value={brandForm.logoUrl ?? ''}
                      onChange={(e) => setBrandForm({ ...brandForm, logoUrl: e.target.value })}
                      placeholder="https://…/logo.png"
                      className="w-full rounded-lg border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Primary color (drives the whole theme)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={brandForm.primaryColor || '#3b82f6'}
                        onChange={(e) =>
                          setBrandForm({ ...brandForm, primaryColor: e.target.value })
                        }
                        className="h-9 w-12 rounded border-gray-300 p-0.5 cursor-pointer"
                      />
                      <input
                        value={brandForm.primaryColor ?? ''}
                        onChange={(e) =>
                          setBrandForm({ ...brandForm, primaryColor: e.target.value })
                        }
                        placeholder="#011F5B"
                        className="w-28 rounded-lg border-gray-300 text-sm font-mono focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Accent color (optional)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={brandForm.accentColor || '#3b82f6'}
                        onChange={(e) =>
                          setBrandForm({ ...brandForm, accentColor: e.target.value })
                        }
                        className="h-9 w-12 rounded border-gray-300 p-0.5 cursor-pointer"
                      />
                      <input
                        value={brandForm.accentColor ?? ''}
                        onChange={(e) =>
                          setBrandForm({ ...brandForm, accentColor: e.target.value })
                        }
                        placeholder="#990000"
                        className="w-28 rounded-lg border-gray-300 text-sm font-mono focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2 flex justify-end gap-2">
                    <button
                      onClick={() => setBrandFor(null)}
                      className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => saveBranding(org.orgId)}
                      disabled={savingBrand}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-xs font-medium text-white transition-colors"
                    >
                      {savingBrand && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      Save branding
                    </button>
                  </div>
                </div>
              )}

              {inviteFor === org.orgId && (
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  <input
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    type="email"
                    placeholder="member@example.org"
                    className="flex-1 min-w-[200px] max-w-sm rounded-lg border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as 'member' | 'orgAdmin')}
                    className="rounded-lg border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500 py-2 px-3"
                  >
                    <option value="member">Member</option>
                    <option value="orgAdmin">Administrator</option>
                  </select>
                  <button
                    onClick={() => handleInvite(org.orgId, inviteEmail.trim(), inviteRole)}
                    disabled={inviting || !inviteEmail.trim()}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm font-medium text-white transition-colors"
                  >
                    {inviting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Send invite
                  </button>
                </div>
              )}

              {(org.members?.length ?? 0) > 0 && (
                <div className="mt-3 border-t border-gray-100 pt-3 space-y-2">
                  <p className="text-xs font-medium text-gray-600">Members</p>
                  {org.members!.map((m) => (
                    <div key={m.userId} className="flex items-center gap-2 text-xs text-gray-700">
                      <Users className="w-3.5 h-3.5 text-gray-400" />
                      <span className="font-medium">{m.displayName || m.email || m.userId}</span>
                      {m.displayName && m.email && <span className="text-gray-500">{m.email}</span>}
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                        {m.orgRole === 'orgAdmin' ? 'Admin' : 'Member'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {org.pendingInvites.length > 0 && (
                <div className="mt-3 border-t border-gray-100 pt-3 space-y-2">
                  <p className="text-xs font-medium text-gray-600">Pending invitations</p>
                  {org.pendingInvites.map((inv) => (
                    <div key={inv.token} className="flex items-center gap-2 text-xs text-gray-700">
                      <Mail className="w-3.5 h-3.5 text-gray-400" />
                      <span className="font-medium">{inv.invitedEmail}</span>
                      {inv.orgRole && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                          {inv.orgRole === 'orgAdmin' ? 'Admin' : 'Member'}
                        </span>
                      )}
                      {inv.expired ? (
                        <span className="text-amber-600">expired — re-invite to refresh</span>
                      ) : (
                        <CopyButton text={acceptLink(org.orgId, inv.token)} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
